import { types } from 'src/routes/trip-tracer/data/trip-tracer.types';
import {
    DEFAULT_PLAYBACK_SPEED,
    PLAYBACK_SPEEDS,
    PLAYBACK_STATUS,
} from 'src/routes/trip-tracer/data/tripTracerPlayback';
import {
    applyTripTracerSelection,
    getTripTracerQueryInitialState,
    TRIP_TRACER_QUERY_MODE,
    isTripTracerCatalogStale,
    TRIP_TRACER_CATALOG_KEYS,
} from 'src/routes/trip-tracer/data/tripTracerCascade';

const getAgencyInitialState = () => ({
    loading: false,
    error: '',
    token: null,
    resolved: false,
    // null after resolution means no capability was resolved for the portal agency.
    item: null,
});

const getAvlDatesInitialState = () => ({
    loading: false,
    error: '',
    token: null,
    // true once the current request succeeded; a canceled request stays false
    // so the same month or date can be requested again.
    loaded: false,
    month: null,
    items: [],
});

const getVehiclesInitialState = () => ({
    loading: false,
    error: '',
    token: null,
    loaded: false,
    serviceDate: null,
    items: [],
});

const getRoutesInitialState = () => ({
    loading: false,
    error: '',
    token: null,
    loaded: false,
    serviceDate: null,
    items: [],
});

/** GTFS shape of the selected route (route mode). */
const getRouteShapeInitialState = () => ({
    loading: false,
    error: null,
    token: null,
    // true once the current request succeeded; a canceled one can be retried.
    loaded: false,
    routeId: null,
    shapes: [],
});

/** GTFS stops for the loaded replay's routes (Bus Stops layer). */
const getStopsInitialState = () => ({
    loading: false,
    error: null,
    token: null,
    // The route ids requested, comma-joined.
    key: null,
    stops: [],
    truncated: false,
});

/** `status` of the AVL replay. */
export const REPLAY_STATUS = Object.freeze({
    IDLE: 'idle',
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error',
});

const getReplayInitialState = () => ({
    status: REPLAY_STATUS.IDLE,
    token: null,
    // buildTripTracerReplayKey of the query being loaded or shown.
    key: null,
    // Normalized TripTracerError when status is error.
    error: null,
    // Adapted TripTracerReplay when status is success.
    data: null,
});

const getPlaybackInitialState = (speed = DEFAULT_PLAYBACK_SPEED) => ({
    status: PLAYBACK_STATUS.STOPPED,
    frameIndex: 0,
    speed,
});

/**
 * Map display choices. They persist across queries; only the per-result
 * choices (hidden and followed vehicles) reset with the replay.
 */
export const getDisplayInitialState = () => ({
    hiddenVehicleIds: [],
    satellite: false,
    showStops: false,
    showRouteShape: true,
    colorBySpeed: false,
    progressivePath: false,
    trackVehicle: true,
    // The vehicle picked on the map to follow; per result, like hiddenVehicleIds.
    followedVehicleId: null,
    // Color-by-speed preset and thresholds; null is the default.
    speedSettings: null,
});

const DISPLAY_KEYS = Object.keys(getDisplayInitialState());

/**
 * A replay change (new request, result, or cleared result) restarts playback
 * and drops per-result selections; speed and display choices are kept.
 */
const withFreshReplayView = (state) => ({
    ...state,
    playback: getPlaybackInitialState(state.playback?.speed),
    focusedPointIndex: null,
    // Stops are fetched for the replay's routes.
    stops: getStopsInitialState(),
    display: { ...state.display, hiddenVehicleIds: [], followedVehicleId: null },
});

const CATALOG_INITIAL_STATES = {
    avlDates: getAvlDatesInitialState,
    vehicles: getVehiclesInitialState,
    routes: getRoutesInitialState,
    routeShape: getRouteShapeInitialState,
    replay: getReplayInitialState,
};

/**
 * Trip Tracer state. Follow-on stories add their slices here; everything in
 * this object is cleared by RESET_TRIP_TRACER.
 *
 * Selections cascade agency → month → date → vehicle; what each change clears
 * is defined once in tripTracerCascade.js.
 */
export const getTripTracerInitialState = () => ({
    agency: getAgencyInitialState(),
    query: getTripTracerQueryInitialState(),
    avlDates: getAvlDatesInitialState(),
    vehicles: getVehiclesInitialState(),
    routes: getRoutesInitialState(),
    routeShape: getRouteShapeInitialState(),
    stops: getStopsInitialState(),
    replay: getReplayInitialState(),
    playback: getPlaybackInitialState(),
    // Replay `index` of the point shown in the popup / selected in records.
    focusedPointIndex: null,
    display: getDisplayInitialState(),
});

/** Applies a selection through the cascade and clears catalogs it invalidates. */
const withSelection = (state, field, value) => {
    const query = applyTripTracerSelection(state.query, field, value);
    if (query === state.query) {
        return state;
    }

    const clearedCatalogs = Object.fromEntries(
        Object.keys(TRIP_TRACER_CATALOG_KEYS)
            .filter((catalog) => isTripTracerCatalogStale(catalog, state.query, query))
            .map((catalog) => [catalog, CATALOG_INITIAL_STATES[catalog]()]),
    );

    const next = { ...state, ...clearedCatalogs, query };
    return clearedCatalogs.replay ? withFreshReplayView(next) : next;
};

const isCurrent = (slice, action) => slice?.token != null && slice.token === action.payload?.token;

const tripTracerReducer = (state = getTripTracerInitialState(), action = {}) => {
    switch (action.type) {
        case types.RESET_TRIP_TRACER:
            return getTripTracerInitialState();

        case types.AGENCY_REQUEST:
            return {
                ...state,
                agency: {
                    ...getAgencyInitialState(),
                    loading: true,
                    token: action.payload.token,
                },
            };

        case types.AGENCY_SUCCESS:
            if (!isCurrent(state.agency, action)) return state;
            return {
                ...state,
                agency: {
                    ...state.agency,
                    loading: false,
                    error: '',
                    resolved: true,
                    item: action.payload.agency ?? null,
                },
            };

        case types.AGENCY_ERROR:
            if (!isCurrent(state.agency, action)) return state;
            return {
                ...state,
                agency: {
                    ...state.agency,
                    loading: false,
                    error: action.payload.error ?? '',
                },
            };

        case types.AVL_DATES_REQUEST:
            return {
                ...withSelection(state, 'month', action.payload.month),
                avlDates: {
                    ...getAvlDatesInitialState(),
                    loading: true,
                    token: action.payload.token,
                    month: action.payload.month,
                },
            };

        case types.AVL_DATES_SUCCESS:
            if (!isCurrent(state.avlDates, action)) return state;
            return {
                ...state,
                avlDates: {
                    ...state.avlDates,
                    loading: false,
                    error: '',
                    loaded: true,
                    items: action.payload.dates ?? [],
                },
            };

        case types.AVL_DATES_ERROR:
            if (!isCurrent(state.avlDates, action)) return state;
            return {
                ...state,
                avlDates: {
                    ...state.avlDates,
                    loading: false,
                    error: action.payload.error ?? '',
                },
            };

        case types.VEHICLES_REQUEST:
            return {
                ...withSelection(state, 'serviceDate', action.payload.serviceDate),
                vehicles: {
                    ...getVehiclesInitialState(),
                    loading: true,
                    token: action.payload.token,
                    serviceDate: action.payload.serviceDate,
                },
            };

        case types.VEHICLES_SUCCESS:
            if (!isCurrent(state.vehicles, action)) return state;
            return {
                ...state,
                vehicles: {
                    ...state.vehicles,
                    loading: false,
                    error: '',
                    loaded: true,
                    items: action.payload.vehicles ?? [],
                },
            };

        case types.VEHICLES_ERROR:
            if (!isCurrent(state.vehicles, action)) return state;
            return {
                ...state,
                vehicles: {
                    ...state.vehicles,
                    loading: false,
                    error: action.payload.error ?? '',
                },
            };

        case types.ROUTES_REQUEST:
            return {
                ...withSelection(state, 'serviceDate', action.payload.serviceDate),
                routes: {
                    ...getRoutesInitialState(),
                    loading: true,
                    token: action.payload.token,
                    serviceDate: action.payload.serviceDate,
                },
            };

        case types.ROUTES_SUCCESS:
            if (!isCurrent(state.routes, action)) return state;
            return {
                ...state,
                routes: {
                    ...state.routes,
                    loading: false,
                    error: '',
                    loaded: true,
                    items: action.payload.routes ?? [],
                },
            };

        case types.ROUTES_ERROR:
            if (!isCurrent(state.routes, action)) return state;
            return {
                ...state,
                routes: {
                    ...state.routes,
                    loading: false,
                    error: action.payload.error ?? '',
                },
            };

        case types.ROUTE_SHAPE_REQUEST:
            return {
                ...state,
                routeShape: {
                    ...getRouteShapeInitialState(),
                    loading: true,
                    token: action.payload.token,
                    routeId: action.payload.routeId,
                },
            };

        case types.ROUTE_SHAPE_SUCCESS:
            if (!isCurrent(state.routeShape, action)) return state;
            return {
                ...state,
                routeShape: {
                    ...state.routeShape, loading: false, loaded: true, shapes: action.payload.shapes ?? [],
                },
            };

        case types.ROUTE_SHAPE_ERROR:
            if (!isCurrent(state.routeShape, action)) return state;
            return { ...state, routeShape: { ...state.routeShape, loading: false, error: action.payload.error ?? null } };

        case types.STOPS_REQUEST:
            return {
                ...state,
                stops: {
                    ...getStopsInitialState(),
                    loading: true,
                    token: action.payload.token,
                    key: action.payload.key,
                },
            };

        case types.STOPS_SUCCESS:
            if (!isCurrent(state.stops, action)) return state;
            return {
                ...state,
                stops: {
                    ...state.stops,
                    loading: false,
                    stops: action.payload.stops ?? [],
                    truncated: action.payload.truncated === true,
                },
            };

        case types.STOPS_ERROR:
            if (!isCurrent(state.stops, action)) return state;
            return { ...state, stops: { ...state.stops, loading: false, error: action.payload.error ?? null } };

        case types.SET_QUERY_MODE:
            if (!Object.values(TRIP_TRACER_QUERY_MODE).includes(action.payload.mode)) return state;
            return withSelection(state, 'mode', action.payload.mode);

        case types.SELECT_ROUTE:
            return withSelection(state, 'routeId', action.payload.routeId ?? null);

        case types.SELECT_VEHICLE:
            return withSelection(state, 'vehicleId', action.payload.vehicleId ?? null);

        case types.CLEAR_SERVICE_DATE:
            return withSelection(state, 'serviceDate', null);

        case types.SET_TIME_WINDOW:
            return withSelection(
                withSelection(state, 'startTime', action.payload.startTime ?? null),
                'endTime',
                action.payload.endTime ?? null,
            );

        case types.REPLAY_REQUEST:
            return {
                ...withFreshReplayView(state),
                replay: {
                    ...getReplayInitialState(),
                    status: REPLAY_STATUS.LOADING,
                    token: action.payload.token,
                    key: action.payload.key,
                },
            };

        case types.REPLAY_SUCCESS:
            if (!isCurrent(state.replay, action)) return state;
            return {
                ...withFreshReplayView(state),
                replay: {
                    ...state.replay,
                    status: REPLAY_STATUS.SUCCESS,
                    error: null,
                    data: action.payload.replay,
                },
            };

        case types.REPLAY_ERROR:
            if (!isCurrent(state.replay, action)) return state;
            return {
                ...state,
                replay: {
                    ...state.replay,
                    status: REPLAY_STATUS.ERROR,
                    error: action.payload.error,
                    data: null,
                },
            };

        case types.REPLAY_CANCELED:
            if (!isCurrent(state.replay, action)) return state;
            return { ...withFreshReplayView(state), replay: getReplayInitialState() };

        case types.SET_PLAYBACK_STATUS:
            if (!Object.values(PLAYBACK_STATUS).includes(action.payload.status)) return state;
            return {
                ...state,
                playback: {
                    ...state.playback,
                    status: action.payload.status,
                    // Stop rewinds to the first frame, like the prototype.
                    frameIndex: action.payload.status === PLAYBACK_STATUS.STOPPED ? 0 : state.playback.frameIndex,
                },
            };

        case types.SET_PLAYHEAD:
            if (!Number.isInteger(action.payload.frameIndex) || action.payload.frameIndex < 0) return state;
            return { ...state, playback: { ...state.playback, frameIndex: action.payload.frameIndex } };

        case types.SET_PLAYBACK_SPEED:
            if (!PLAYBACK_SPEEDS.includes(action.payload.speed)) return state;
            return { ...state, playback: { ...state.playback, speed: action.payload.speed } };

        case types.FOCUS_POINT:
            return { ...state, focusedPointIndex: action.payload.pointIndex ?? null };

        case types.SET_DISPLAY_OPTION:
            if (!DISPLAY_KEYS.includes(action.payload.key)) return state;
            return { ...state, display: { ...state.display, [action.payload.key]: action.payload.value } };

        case types.SET_HIDDEN_VEHICLES: {
            const hiddenVehicleIds = [...new Set(action.payload.vehicleIds ?? [])];
            return {
                ...state,
                // Hiding vehicles changes the timeline, so playback restarts.
                playback: getPlaybackInitialState(state.playback.speed),
                focusedPointIndex: null,
                display: {
                    ...state.display,
                    hiddenVehicleIds,
                    // A hidden vehicle cannot be followed.
                    followedVehicleId: hiddenVehicleIds.includes(state.display.followedVehicleId)
                        ? null
                        : state.display.followedVehicleId,
                },
            };
        }

        default:
            return state;
    }
};

export default tripTracerReducer;
